import React, { useState, useEffect, useRef } from 'react';
import FloatingDock from '@/components/layout/FloatingDock';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Video, VideoOff, Mic, MicOff, Phone, PhoneOff, Users, Plus, 
  MessageSquare, Send, Settings, X, Copy, Check, Share2, 
  BadgeCheck, Shield, ArrowLeft, MoreVertical, Crown, Lock
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Link, useParams, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Talkspace = ({ user }) => {
  const navigate = useNavigate();
  const { roomId } = useParams();
  
  // Room states
  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Create room modal
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [newRoom, setNewRoom] = useState({ name: '', description: '', isPrivate: false });
  const [creating, setCreating] = useState(false);
  
  // Chat states
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [participants, setParticipants] = useState([]);
  
  // Video/Audio states
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [inCall, setInCall] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  
  // WebSocket
  const wsRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideosRef = useRef({});
  
  // Invite link
  const [showInvite, setShowInvite] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchRooms();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (roomId) {
      joinRoom(roomId);
    }
  }, [roomId]);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API_URL}/talkspace/rooms`, { withCredentials: true });
      setRooms(response.data || []);
    } catch (error) {
      // If endpoint doesn't exist yet, use mock data
      setRooms([
        { room_id: 'general', name: 'General Chat', description: 'Open chat for everyone', participants_count: 0, is_private: false, owner: { name: 'Socyy', is_verified: true } },
        { room_id: 'creators', name: 'Creators Lounge', description: 'For verified creators only', participants_count: 0, is_private: false, owner: { name: 'Socyy', is_verified: true } },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const createRoom = async () => {
    if (!newRoom.name.trim()) {
      toast.error('Room name is required');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/talkspace/rooms`, newRoom, { withCredentials: true });
      setRooms([response.data, ...rooms]);
      setShowCreateRoom(false);
      setNewRoom({ name: '', description: '', isPrivate: false });
      toast.success('Room created!');
      joinRoom(response.data.room_id);
    } catch (error) {
      // Mock create for demo
      const mockRoom = {
        room_id: `room_${Date.now()}`,
        name: newRoom.name,
        description: newRoom.description,
        is_private: newRoom.isPrivate,
        participants_count: 1,
        owner: { name: user?.name, user_id: user?.user_id, is_verified: user?.is_verified }
      };
      setRooms([mockRoom, ...rooms]);
      setShowCreateRoom(false);
      setNewRoom({ name: '', description: '', isPrivate: false });
      toast.success('Room created!');
      joinRoom(mockRoom.room_id);
    } finally {
      setCreating(false);
    }
  };

  const joinRoom = async (roomId) => {
    setCurrentRoom(rooms.find(r => r.room_id === roomId) || { room_id: roomId, name: 'Chat Room' });
    setMessages([]);
    setParticipants([{ ...user, isHost: true }]);
    
    // Connect to WebSocket for real-time chat
    const wsUrl = API_URL.replace('http', 'ws').replace('/api', '') + `/api/talkspace/ws/${roomId}`;
    try {
      wsRef.current = new WebSocket(wsUrl);
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'message') {
          setMessages(prev => [...prev, data.message]);
        } else if (data.type === 'participant_joined') {
          setParticipants(prev => [...prev, data.user]);
        } else if (data.type === 'participant_left') {
          setParticipants(prev => prev.filter(p => p.user_id !== data.user_id));
        }
      };
    } catch (e) {
      console.log('WebSocket not available, using mock mode');
    }
  };

  const leaveRoom = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    setCurrentRoom(null);
    setMessages([]);
    setParticipants([]);
    setInCall(false);
    setShowVideo(false);
    navigate('/talkspace');
  };

  const sendMessage = () => {
    if (!newMessage.trim()) return;
    
    const message = {
      message_id: `msg_${Date.now()}`,
      content: newMessage,
      sender: { name: user?.name, user_id: user?.user_id, avatar: user?.avatar, is_verified: user?.is_verified },
      created_at: new Date().toISOString()
    };
    
    // Send via WebSocket or add locally
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'message', content: newMessage }));
    }
    setMessages(prev => [...prev, message]);
    setNewMessage('');
  };

  const toggleVideo = async () => {
    if (!isVideoEnabled) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: isAudioEnabled });
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        setIsVideoEnabled(true);
        setShowVideo(true);
        setInCall(true);
      } catch (error) {
        toast.error('Could not access camera');
      }
    } else {
      if (localVideoRef.current?.srcObject) {
        localVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
      }
      setIsVideoEnabled(false);
    }
  };

  const toggleAudio = () => {
    setIsAudioEnabled(!isAudioEnabled);
    if (localVideoRef.current?.srcObject) {
      const audioTrack = localVideoRef.current.srcObject.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !isAudioEnabled;
      }
    }
  };

  const endCall = () => {
    if (localVideoRef.current?.srcObject) {
      localVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setInCall(false);
    setIsVideoEnabled(false);
    setShowVideo(false);
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}/talkspace/${currentRoom?.room_id}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Invite link copied!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-32" data-testid="talkspace-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">
        
        {!currentRoom ? (
          // Room List View
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Video className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-heading font-black text-gray-900 dark:text-white">
                    Talkspace
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Live chat rooms & video meetings</p>
                </div>
              </div>
              <Button
                onClick={() => setShowCreateRoom(true)}
                className="rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                data-testid="create-room-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Room
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loading ? (
                <div className="col-span-full flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : rooms.length === 0 ? (
                <div className="col-span-full">
                  <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md text-center">
                    <Video className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">No rooms yet</h3>
                    <p className="text-gray-500 mb-4">Create a room to start chatting with others</p>
                    <Button onClick={() => setShowCreateRoom(true)} className="rounded-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Room
                    </Button>
                  </Card>
                </div>
              ) : (
                rooms.map((room) => (
                  <motion.div
                    key={room.room_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ y: -4 }}
                  >
                    <Card 
                      className="p-5 rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md cursor-pointer hover:shadow-2xl transition-all"
                      onClick={() => joinRoom(room.room_id)}
                      data-testid={`room-${room.room_id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${room.is_private ? 'bg-gray-200 dark:bg-gray-700' : 'bg-gradient-to-br from-purple-500 to-pink-500'}`}>
                            {room.is_private ? <Lock className="w-5 h-5 text-gray-600 dark:text-gray-300" /> : <MessageSquare className="w-5 h-5 text-white" />}
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 dark:text-white">{room.name}</h3>
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <span>by {room.owner?.name}</span>
                              {room.owner?.is_verified && <BadgeCheck className="w-3 h-3 text-blue-500" />}
                            </div>
                          </div>
                        </div>
                        {room.is_private && (
                          <span className="text-xs bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded-full">Private</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{room.description || 'No description'}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Users className="w-4 h-4" />
                          <span>{room.participants_count || 0} online</span>
                        </div>
                        <Button size="sm" className="rounded-full bg-primary hover:bg-primary-700 text-white">
                          Join
                        </Button>
                      </div>
                    </Card>
                  </motion.div>
                ))
              )}
            </div>
          </>
        ) : (
          // Active Room View
          <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-200px)]">
            {/* Main Chat/Video Area */}
            <div className="flex-1 flex flex-col">
              {/* Room Header */}
              <Card className="p-4 rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={leaveRoom} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                      <h2 className="font-bold text-gray-900 dark:text-white">{currentRoom.name}</h2>
                      <p className="text-sm text-gray-500">{participants.length} participant{participants.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setShowInvite(true)}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                    >
                      <Share2 className="w-4 h-4 mr-1" />
                      Invite
                    </Button>
                    <Button
                      onClick={leaveRoom}
                      variant="destructive"
                      size="sm"
                      className="rounded-full"
                    >
                      Leave
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Video Area (when in call) */}
              {showVideo && (
                <Card className="mb-4 p-4 rounded-2xl border border-white/20 shadow-xl bg-gray-900 aspect-video relative overflow-hidden">
                  <video
                    ref={localVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover rounded-xl"
                  />
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/50 rounded-full px-4 py-2">
                    <button
                      onClick={toggleAudio}
                      className={`p-2 rounded-full ${isAudioEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-500'}`}
                    >
                      {isAudioEnabled ? <Mic className="w-5 h-5 text-white" /> : <MicOff className="w-5 h-5 text-white" />}
                    </button>
                    <button
                      onClick={toggleVideo}
                      className={`p-2 rounded-full ${isVideoEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-500'}`}
                    >
                      {isVideoEnabled ? <Video className="w-5 h-5 text-white" /> : <VideoOff className="w-5 h-5 text-white" />}
                    </button>
                    <button
                      onClick={endCall}
                      className="p-2 rounded-full bg-red-500 hover:bg-red-600"
                    >
                      <PhoneOff className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </Card>
              )}

              {/* Chat Area */}
              <Card className="flex-1 flex flex-col rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md overflow-hidden">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                      <MessageSquare className="w-12 h-12 mb-2 opacity-50" />
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div key={msg.message_id} className="flex items-start gap-2">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={msg.sender?.avatar} />
                          <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-xs font-bold">
                            {msg.sender?.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-1">
                            <span className="font-semibold text-sm text-gray-900 dark:text-white">{msg.sender?.name}</span>
                            {msg.sender?.is_verified && <BadgeCheck className="w-3 h-3 text-blue-500" />}
                            <span className="text-xs text-gray-400">
                              {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          <p className="text-gray-700 dark:text-gray-300">{msg.content}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Message Input */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex gap-2">
                    {!inCall && (
                      <Button
                        onClick={toggleVideo}
                        variant="outline"
                        className="rounded-full"
                        title="Start Video"
                      >
                        <Video className="w-4 h-4" />
                      </Button>
                    )}
                    <Input
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      className="flex-1 rounded-xl"
                      data-testid="talkspace-message-input"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={!newMessage.trim()}
                      className="rounded-full bg-primary hover:bg-primary-700 text-white"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            </div>

            {/* Participants Sidebar */}
            <Card className="w-full lg:w-72 p-4 rounded-2xl border border-white/20 shadow-xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-md h-fit lg:h-auto">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Participants ({participants.length})
              </h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {participants.map((p, idx) => (
                  <div key={p.user_id || idx} className="flex items-center gap-2 p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={p.avatar} />
                      <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-xs font-bold">
                        {p.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <span className="font-semibold text-sm text-gray-900 dark:text-white truncate">{p.name}</span>
                        {p.is_verified && <BadgeCheck className="w-3 h-3 text-blue-500" />}
                        {p.is_admin && <Shield className="w-3 h-3 text-red-500" />}
                      </div>
                      {p.isHost && (
                        <span className="text-xs text-yellow-600 flex items-center gap-1">
                          <Crown className="w-3 h-3" /> Host
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Create Room Modal */}
      <AnimatePresence>
        {showCreateRoom && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateRoom(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-3xl p-6 w-full max-w-md shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create Room</h2>
                <button onClick={() => setShowCreateRoom(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Room Name *</label>
                  <Input
                    value={newRoom.name}
                    onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
                    placeholder="e.g., Team Standup"
                    className="rounded-xl"
                    data-testid="room-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <Textarea
                    value={newRoom.description}
                    onChange={(e) => setNewRoom({ ...newRoom, description: e.target.value })}
                    placeholder="What's this room about?"
                    className="rounded-xl resize-none"
                    rows={3}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">Private Room</p>
                    <p className="text-sm text-gray-500">Only invited members can join</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newRoom.isPrivate}
                      onChange={(e) => setNewRoom({ ...newRoom, isPrivate: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                <Button
                  onClick={createRoom}
                  disabled={creating || !newRoom.name.trim()}
                  className="w-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold h-12"
                  data-testid="create-room-submit"
                >
                  {creating ? 'Creating...' : 'Create Room'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite Modal */}
      <AnimatePresence>
        {showInvite && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowInvite(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-3xl p-6 w-full max-w-md shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Invite People</h2>
                <button onClick={() => setShowInvite(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-gray-600 dark:text-gray-400 mb-4">Share this link to invite people to the room:</p>
              
              <div className="flex gap-2">
                <Input
                  value={`${window.location.origin}/talkspace/${currentRoom?.room_id}`}
                  readOnly
                  className="rounded-xl flex-1"
                />
                <Button
                  onClick={copyInviteLink}
                  className="rounded-full"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <FloatingDock user={user} />
    </div>
  );
};

export default Talkspace;
